import { createMachine, assign } from 'xstate';

import { convertProfilesToProviders } from 'Src/behaviors/locations/utils';
import { EVENTS, LocationContext, LocationEvent, STATES } from './types';
import { assertEventType } from '../utils';

export const createLocationV9Machine = (initialContext: LocationContext) =>
  createMachine<LocationContext, LocationEvent>(
    {
      id: 'location-v9',
      context: {
        currentQuery: initialContext.currentQuery,
        fetchLocation: initialContext.fetchLocation,
        fetchProvidersByLocation: initialContext.fetchProvidersByLocation,
        location: initialContext.location,
        locationId: initialContext.locationId,
        providersPage: initialContext.providersPage,
        providers: initialContext.providers,
        totalProviders: initialContext.totalProviders,
        searchSDK: initialContext.searchSDK
      },
      type: 'parallel',
      states: {
        [STATES.location]: {
          initial: STATES.initializing,
          states: {
            [STATES.initializing]: {
              always: [
                { target: STATES.success, cond: 'hasLocationInContext' },
                { target: STATES.pending, cond: 'hasLocationIdInContext' },
                { target: STATES.error }
              ]
            },
            [STATES.pending]: {
              invoke: {
                id: 'fetchLocation',
                src: 'fetchLocation',
                onDone: {
                  target: STATES.success,
                  actions: 'onFetchLocationSuccess'
                },
                onError: {
                  target: STATES.error,
                  actions: 'onFetchLocationError'
                }
              }
            },
            [STATES.error]: {},
            [STATES.success]: {}
          }
        },
        [STATES.providers]: {
          initial: STATES.initializing,
          states: {
            [STATES.initializing]: {
              always: [
                { target: STATES.success, cond: 'hasProvidersInContext' },
                { target: STATES.pending, cond: 'hasLocationIdInContext' },
                { target: STATES.error }
              ]
            },
            [STATES.pending]: {
              invoke: {
                id: 'fetchProviders',
                src: 'fetchProviders',
                onDone: {
                  target: STATES.success,
                  actions: 'onFetchProvidersSuccess'
                },
                onError: {
                  target: STATES.error,
                  actions: 'onFetchProvidersError'
                }
              }
            },
            [STATES.error]: {},
            [STATES.success]: {}
          }
        }
      },
      on: {
        [EVENTS.loadMoreProviders]: {
          target: `${STATES.providers}.${STATES.pending}`,
          actions: 'incrementPage'
        },
        [EVENTS.newProvidersSearch]: {
          target: `${STATES.providers}.${STATES.pending}`,
          actions: 'setCurrentQuery'
        }
      }
    },
    {
      actions: {
        onFetchLocationSuccess: assign({
          location: (_ctx, event) => {
            assertEventType(event, EVENTS.fetchLocationSuccess);
            return event.data;
          }
        }),
        onFetchLocationError: assign({
          error: (_ctx, event) => {
            assertEventType(event, EVENTS.fetchLocationError);
            return event;
          }
        }),
        onFetchProvidersSuccess: assign({
          providers: (_ctx, event) => {
            assertEventType(event, EVENTS.fetchProvidersSuccess);
            return convertProfilesToProviders(
              event.data._result,
              _ctx.locationId
            );
          },
          totalProviders: (_ctx, event) => {
            assertEventType(event, EVENTS.fetchProvidersSuccess);
            return event.data._metadata.total_providers;
          }
        }),
        onFetchProvidersError: assign({
          providersError: (_ctx, event) => {
            assertEventType(event, EVENTS.fetchProvidersError);
            return event;
          }
        }),
        incrementPage: assign({
          providersPage: (ctx) => {
            return ctx.providersPage + 1;
          }
        }),
        setCurrentQuery: assign({
          currentQuery: (_ctx, event) => {
            assertEventType(event, EVENTS.newProvidersSearch);
            return event.data.currentQuery;
          }
        })
      },
      guards: {
        hasLocationIdInContext: (ctx) => Boolean(ctx.locationId),
        hasLocationInContext: (ctx) => Boolean(ctx.location),
        hasProvidersInContext: (ctx) => Boolean(ctx.providers)
      },
      services: {
        fetchLocation: (ctx) => {
          const { locationId, fetchLocation, searchSDK } = ctx;
          // this is the same fetch function that redux uses, modified slightly
          // so it returns the data rather than just call dispatch. it returns
          // a Promise, so works as-is as a service in xstate
          return fetchLocation({ locationId, searchSDK });
        },
        fetchProviders: (context) => {
          const {
            locationId,
            fetchProvidersByLocation,
            currentQuery,
            providersPage
          } = context;
          return fetchProvidersByLocation(locationId, {
            ...currentQuery,
            page: providersPage
          });
        }
      }
    }
  );
